import { useState } from 'react'

export const QuantumLearningCurve = () => {
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<any>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [formData, setFormData] = useState({
    technology: 'quantum-computing',
    adoptionPhase: 'early',
    marketSize: '10B',
    timeframe: '5y'
  })

  const handleAnalyze = async () => {
    setLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      const baseCostDecline = Math.floor(Math.random() * 25) + 15 // 15-40%
      const learningRate = Math.floor(Math.random() * 30) + 60 // 60-90%
      
      setResults({
        technology: formData.technology.replace('-', ' ').toUpperCase(),
        metrics: {
          costDecline: baseCostDecline,
          learningRate: learningRate,
          adoptionSpeed: Math.floor(Math.random() * 20) + 65,
          marketPenetration: Math.floor(Math.random() * 15) + 25,
          efficiencyGain: Math.floor(Math.random() * 35) + 45
        },
        projections: [
          { year: '2025', cost: 100, efficiency: 100 },
          { year: '2026', cost: 100 - baseCostDecline * 0.3, efficiency: 100 + (learningRate * 0.2) },
          { year: '2027', cost: 100 - baseCostDecline * 0.6, efficiency: 100 + (learningRate * 0.4) },
          { year: '2028', cost: 100 - baseCostDecline * 0.8, efficiency: 100 + (learningRate * 0.6) },
          { year: '2029', cost: 100 - baseCostDecline, efficiency: 100 + (learningRate * 0.8) },
          { year: '2030', cost: 100 - baseCostDecline * 1.2, efficiency: 100 + learningRate }
        ],
        riskFactors: [
          'Regulatory uncertainty',
          'Technical scalability challenges', 
          'Competition from alternative technologies',
          'Supply chain constraints'
        ],
        opportunities: [
          'First-mover advantage',
          'Patent portfolio development',
          'Strategic partnerships',
          'Government incentives'
        ]
      })
    } catch (error) {
      console.error('Analysis failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-none space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Quantum Cost-Decline Learning Curve Model</h1>
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="w-6 h-6 bg-blue-100 hover:bg-blue-200 rounded-full flex items-center justify-center text-blue-600 text-sm font-medium transition-colors"
            >
              ?
            </button>
            {showTooltip && (
              <div className="absolute z-10 w-80 p-3 mt-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg">
                <div className="font-medium mb-1">What does this module do?</div>
                <div className="text-gray-300">
                  Models technology adoption curves and cost efficiency improvements over time using learning curve mathematics. 
                  Predicts how emerging technologies (quantum computing, AI chips, batteries) will scale, become cheaper, 
                  and gain market adoption based on historical patterns and current adoption phase.
                </div>
                <div className="absolute top-0 left-6 -mt-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
              </div>
            )}
          </div>
        </div>
        <p className="text-gray-600 mt-2">
          Technology adoption patterns and cost efficiency projections
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Model Parameters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Technology Sector
            </label>
            <select 
              value={formData.technology}
              onChange={(e) => setFormData({...formData, technology: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="quantum-computing">Quantum Computing</option>
              <option value="ai-chips">AI Processors</option>
              <option value="solid-state">Solid State Batteries</option>
              <option value="photonic">Photonic Computing</option>
              <option value="neuromorphic">Neuromorphic Chips</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adoption Phase
            </label>
            <select 
              value={formData.adoptionPhase}
              onChange={(e) => setFormData({...formData, adoptionPhase: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="research">Research Phase</option>
              <option value="early">Early Adoption</option>
              <option value="growth">Growth Phase</option>
              <option value="maturity">Maturity</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Market Size
            </label>
            <select 
              value={formData.marketSize}
              onChange={(e) => setFormData({...formData, marketSize: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="1B">$1B - Niche Market</option>
              <option value="10B">$10B - Growing Market</option>
              <option value="100B">$100B - Large Market</option>
              <option value="1T">$1T+ - Massive Market</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Analysis Timeframe
            </label>
            <select 
              value={formData.timeframe}
              onChange={(e) => setFormData({...formData, timeframe: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="3y">3 Years</option>
              <option value="5y">5 Years</option>
              <option value="10y">10 Years</option>
              <option value="15y">15 Years</option>
            </select>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleAnalyze}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <span>⚡</span>
            {loading ? 'Computing Model...' : 'Generate Learning Curve'}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Learning Curve Metrics</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(results.metrics).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{value as number}%</div>
                  <div className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Projections Chart */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Cost & Efficiency Projections</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Year</th>
                    <th className="text-right py-2">Cost Index</th>
                    <th className="text-right py-2">Efficiency Index</th>
                    <th className="text-right py-2">Cost Change</th>
                    <th className="text-right py-2">Efficiency Gain</th>
                  </tr>
                </thead>
                <tbody>
                  {results.projections.map((proj: any, index: number) => (
                    <tr key={proj.year} className="border-b">
                      <td className="py-2 font-medium">{proj.year}</td>
                      <td className="text-right py-2">{proj.cost.toFixed(1)}</td>
                      <td className="text-right py-2">{proj.efficiency.toFixed(1)}</td>
                      <td className={`text-right py-2 ${index === 0 ? 'text-gray-500' : proj.cost < results.projections[index-1]?.cost ? 'text-green-600' : 'text-red-600'}`}>
                        {index === 0 ? 'baseline' : `${(((proj.cost - results.projections[index-1].cost) / results.projections[index-1].cost) * 100).toFixed(1)}%`}
                      </td>
                      <td className={`text-right py-2 ${index === 0 ? 'text-gray-500' : 'text-green-600'}`}>
                        {index === 0 ? 'baseline' : `+${(proj.efficiency - results.projections[index-1].efficiency).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Risk & Opportunities */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Factors</h3>
              <div className="space-y-2">
                {results.riskFactors.map((risk: string, index: number) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-red-500">⚠️</span>
                    <span className="text-sm text-gray-700">{risk}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Opportunities</h3>
              <div className="space-y-2">
                {results.opportunities.map((opportunity: string, index: number) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-green-500">💡</span>
                    <span className="text-sm text-gray-700">{opportunity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}